import { ContributorDashboardStats, UserUseCase } from "lib/models/userusecase-models";
import { CatalogicalService, UserPermissions } from "lib/services";
import { useCallback, useEffect, useMemo, useState } from "react";

export interface UseContributorDashboardResult {
  useCases: Partial<UserUseCase>[];
  stats: ContributorDashboardStats;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY_STATS: ContributorDashboardStats = {
  totalContributed: 0,
  totalViews: 0,
  totalUsedClicks: 0,
  totalComments: 0,
};

export const useContributorDashboard = (): UseContributorDashboardResult => {
  const [useCases, setUseCases] = useState<Partial<UserUseCase>[]>([]);
  const [stats, setStats] = useState<ContributorDashboardStats>(EMPTY_STATS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const service = useMemo(() => new CatalogicalService<UserUseCase>("userusecase"), []);

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const alias = UserPermissions.current.username || "";

      const response = await service.getItems({
        filters: { contributorAlias: alias },
        sortBy: ["catalogicalGenerated.created.on"],
        sortDirection: ["desc"],
        pageSize: 100,
      });

      const items = response?.data || [];
      const total = items.length;
      setUseCases(items);
      setStats({
        totalContributed: total,
        totalViews: items.reduce((sum, uc) => sum + (uc.viewCount || 0), 0),
        totalUsedClicks: items.reduce((sum, uc) => sum + (uc.usedCount || 0), 0),
        totalComments: items.reduce((sum, uc) => sum + (uc.commentCount || 0), 0),
      });
    } catch (err) {
      console.error("Error fetching contributor dashboard:", err);
      setError("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  }, [service]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { useCases, stats, isLoading, error, refetch: fetchDashboard };
};
